import { useMemo } from 'react';

import { useOrganizationOverviewQuery } from '@cast/shared-cost-data-access';

import {
  getClustersOverviewRangeIso,
  useClustersOverviewDateRange,
} from '@cast/features-cluster-shared';
import type { ResourceType } from '@cast/features-cluster-shared-utils';
import {
  mapOverviewTimeseriesToResourceChart,
  mergeOverviewSections,
} from '@cast/features-cluster-shared-utils';

type UseDashboardResourceChartDataArgs = {
  resourceType: ResourceType;
};

export const useDashboardResourceChartData = ({
  resourceType,
}: UseDashboardResourceChartDataArgs) => {
  const range = useClustersOverviewDateRange();
  const { startTime, endTime } = getClustersOverviewRangeIso(range);
  const { data, isLoading, isFetching, error, refetch } =
    useOrganizationOverviewQuery({
      startTime,
      endTime,
    });

  const merged = useMemo(
    () => mergeOverviewSections(data?.agent, data?.agentless),
    [data?.agent, data?.agentless]
  );

  const { chartData, averages } = useMemo(
    () =>
      mapOverviewTimeseriesToResourceChart(
        merged.resourceTimeseries,
        resourceType
      ),
    [merged.resourceTimeseries, resourceType]
  );

  return {
    chartData,
    averages,
    isLoading,
    error: isFetching ? undefined : error,
    refetch,
  };
};
